const createHeadingId = (
  heading: HTMLElement,
  index: number,
  usedIds: Set<string>,
): string => {
  const textSlug = (heading.textContent ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const baseId = heading.id || textSlug || `sekcja-${index + 1}`;
  let uniqueId = baseId;
  let suffix = 2;

  while (usedIds.has(uniqueId)) {
    uniqueId = `${baseId}-${suffix}`;
    suffix += 1;
  }

  usedIds.add(uniqueId);
  heading.id = uniqueId;

  return uniqueId;
};

export const initContentToc = (): void => {
  document.querySelectorAll<HTMLElement>('[data-content-toc]').forEach((toc) => {
    const wrapper = toc.closest<HTMLElement>('[data-float-sidebar-wrapper]');
    const content = wrapper?.querySelector<HTMLElement>('[data-content-toc-content]');
    const list = toc.querySelector<HTMLOListElement>('[data-content-toc-list]');

    if (!content || !list) return;

    const headings = Array.from(content.querySelectorAll<HTMLElement>('h2, h3'));

    if (!headings.length) {
      toc.closest<HTMLElement>('.content-index-sidebar')?.remove();
      wrapper?.querySelector<HTMLElement>('[data-float-sidebar-relative]')?.classList.add('content-index-body--without-toc');
      return;
    }

    const usedIds = new Set<string>();
    const links = new Map<HTMLElement, HTMLAnchorElement>();
    let currentH2Item: HTMLLIElement | null = null;
    let nestedList: HTMLOListElement | null = null;

    headings.forEach((heading, index) => {
      const id = createHeadingId(heading, index, usedIds);
      const item = document.createElement('li');
      const link = document.createElement('a');

      item.className = `content-index-item content-index-item--${heading.tagName.toLowerCase()}`;
      link.className = 'content-index-link p-s';
      link.href = `#${id}`;
      link.textContent = heading.textContent?.trim() ?? '';
      item.appendChild(link);
      links.set(heading, link);

      link.addEventListener('click', (event: MouseEvent) => {
        event.preventDefault();
        const offset = Number.parseInt(toc.dataset.scrollOffset ?? '0', 10) || 0;
        const top = heading.getBoundingClientRect().top + window.scrollY - offset;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        window.history.replaceState(null, '', `#${id}`);
        window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
      });

      if (heading.tagName === 'H2') {
        currentH2Item = item;
        nestedList = null;
        list.appendChild(item);
        return;
      }

      if (!currentH2Item) {
        list.appendChild(item);
        return;
      }

      if (!nestedList) {
        nestedList = document.createElement('ol');
        nestedList.className = 'content-index-sublist';
        currentH2Item.appendChild(nestedList);
      }

      nestedList.appendChild(item);
    });

    let ticking = false;
    const setActive = (activeHeading: HTMLElement): void => {
      links.forEach((link, heading) => {
        const isActive = heading === activeHeading;
        link.classList.toggle('--active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    };

    const updateActiveHeading = (): void => {
      const offset = (Number.parseInt(toc.dataset.scrollOffset ?? '0', 10) || 0) + 8;
      let activeHeading = headings[0];

      headings.forEach((heading) => {
        if (heading.getBoundingClientRect().top <= offset) {
          activeHeading = heading;
        }
      });

      setActive(activeHeading);
      ticking = false;
    };

    const requestActiveUpdate = (): void => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateActiveHeading);
    };

    window.addEventListener('scroll', requestActiveUpdate, { passive: true });
    window.addEventListener('resize', requestActiveUpdate);
    updateActiveHeading();

    if (window.location.hash) {
      const target = headings.find((heading) => `#${heading.id}` === window.location.hash);
      if (target) {
        window.requestAnimationFrame(() => {
          const offset = Number.parseInt(toc.dataset.scrollOffset ?? '0', 10) || 0;
          window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset });
        });
      }
    }
  });
};
